/**
 * PaymentSchedulePanel — Vue "Paiements à effectuer / effectués" pour un mois de paiement.
 *
 * Règle métier : Le net du mois M est payé le 10 du mois M+1.
 * Ce panneau montre par exemple pour "Juillet 2026" les paies de "Juin 2026"
 * avec leur statut (payé ou à payer) et un bouton de marquage.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, ChevronLeft, ChevronRight, CalendarDays,
  CheckCircle2, MinusCircle, Wallet, Info, Smartphone, Copy, FileDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import MarkPaidDialog from "./MarkPaidDialog";
import { printPaymentSchedulePDF } from "../utils/salaryPdfHelpers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
const fmt = (v) => (Math.round(v || 0)).toLocaleString("fr-FR");

const formatFrDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
};

const copyToClipboard = (text, label) => {
  if (!text) return;
  const t = String(text).replace(/\s+/g, "");
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(t).then(
      () => toast.success(`${label || "Copié"} : ${t}`),
      () => toast.error("Copie impossible"),
    );
  }
};

const PaymentSchedulePanel = ({ currentUser }) => {
  const now = new Date();
  const [payYear, setPayYear] = useState(now.getFullYear());
  const [payMonth, setPayMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payDialog, setPayDialog] = useState(null); // { entry }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/salaries/payment-schedule`, {
        params: { year: payYear, month: payMonth },
      });
      setData(res.data);
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, [payYear, payMonth]);

  useEffect(() => { load(); }, [load]);

  const prev = () => {
    if (payMonth === 1) { setPayMonth(12); setPayYear(y => y - 1); }
    else setPayMonth(m => m - 1);
  };
  const next = () => {
    if (payMonth === 12) { setPayMonth(1); setPayYear(y => y + 1); }
    else setPayMonth(m => m + 1);
  };

  const workMonthLabel = useMemo(() => {
    if (!data) return "—";
    const [y, m] = data.work_period.split("-").map(Number);
    return `${MONTHS[m - 1]} ${y}`;
  }, [data]);

  const [workYear, workMonth] = useMemo(() => {
    if (!data) return [now.getFullYear(), 1];
    const [y, m] = data.work_period.split("-").map(Number);
    return [y, m];
  }, [data, now]);

  const confirmMarkPaid = async ({ payment_date, amount, paid_by, notes }) => {
    if (!payDialog?.entry) return;
    try {
      await axios.post(`${API}/salaries/${payDialog.entry.salary_id}/payslip/${workYear}/${workMonth}/mark-paid`, {
        payment_date, amount, paid_by, notes,
      });
      toast.success(`Paiement enregistré : ${fmt(amount)} F le ${formatFrDate(payment_date)}`);
      load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  };

  const unmarkPaid = async (entry) => {
    if (!window.confirm(`Annuler le paiement de ${entry.name} pour ${workMonthLabel} ?`)) return;
    try {
      await axios.post(`${API}/salaries/${entry.salary_id}/payslip/${workYear}/${workMonth}/unmark-paid`);
      toast.success("Paiement annulé");
      load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  };

  const kpis = [
    { label: "À payer", value: fmt(data?.totals?.unpaid || 0), suffix: "F", accent: "#f59e0b", count: data?.counts?.unpaid || 0 },
    { label: "Déjà payés", value: fmt(data?.totals?.paid || 0), suffix: "F", accent: "#10b981", count: data?.counts?.paid || 0 },
    { label: "Total planifié", value: fmt(data?.totals?.planned || 0), suffix: "F", accent: "#38bdf8", count: data?.counts?.total || 0 },
  ];

  return (
    <div className="space-y-3" data-testid="payment-schedule-panel">
      {/* Top bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
            <Wallet className="w-5 h-5 text-amber-300" strokeWidth={2.3} />
          </div>
          <div className="min-w-0">
            <p className="text-white font-bold text-[15px] leading-tight">Paiements à effectuer</p>
            <p className="text-slate-500 text-[11px] leading-tight">
              Décaissements planifiés pour <b className="text-slate-300">{MONTHS[payMonth - 1]} {payYear}</b>
            </p>
          </div>
        </div>

        {/* Payment month navigation */}
        <div className="flex items-center gap-1 bg-slate-800/60 border border-slate-700 rounded-lg px-1 py-0.5 flex-shrink-0">
          <Button size="sm" variant="ghost" onClick={prev} className="h-7 w-7 p-0 text-slate-300" data-testid="ps-prev-month">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-[12px] font-bold text-white px-2 min-w-[110px] text-center">
            {MONTHS[payMonth - 1]} {payYear}
          </div>
          <Button size="sm" variant="ghost" onClick={next} className="h-7 w-7 p-0 text-slate-300" data-testid="ps-next-month">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <Button
          onClick={() => { const n = new Date(); setPayYear(n.getFullYear()); setPayMonth(n.getMonth() + 1); }}
          size="sm" variant="outline" className="border-slate-600 text-slate-300 h-9 text-[11px]" data-testid="ps-current-month"
        >
          Aujourd&apos;hui
        </Button>
        <Button onClick={load} disabled={loading} size="sm" variant="outline" className="border-slate-600 text-slate-200 h-9 flex-shrink-0">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </Button>
        <Button
          onClick={() => printPaymentSchedulePDF(data, payYear, payMonth)}
          disabled={loading || !data?.entries?.length}
          size="sm"
          className="h-9 bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11.5px] flex-shrink-0"
          title={`PDF échéances ${MONTHS[payMonth - 1]} ${payYear}`}
          data-testid="ps-pdf-month"
        >
          <FileDown className="w-3.5 h-3.5 mr-1" strokeWidth={2.8} /> PDF
        </Button>
      </div>

      {/* Info banner */}
      <div className="rounded-lg bg-blue-500/10 border border-blue-500/30 px-3 py-2 flex items-start gap-2">
        <Info className="w-4 h-4 text-blue-300 flex-shrink-0 mt-0.5" />
        <div className="text-[11.5px] text-blue-200 leading-tight">
          <b>Règle de décalage :</b> Les nets d&apos;un mois M sont payés le <b>10 du mois M+1</b>.
          Ici, la vue montre les nets du travail de <b className="text-white">{workMonthLabel}</b>{" "}
          avec échéance planifiée au{" "}
          <b className="text-emerald-300">{formatFrDate(data?.payment_planned_date)}</b>.
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-3 gap-2">
        {kpis.map((k) => (
          <div
            key={k.label}
            className="rounded-lg bg-slate-800/60 border border-slate-700 p-2.5"
            style={{ borderLeft: `3px solid ${k.accent}` }}
          >
            <p className="text-[9.5px] font-bold text-slate-400 uppercase tracking-wider">{k.label}</p>
            <p className="text-white font-black text-[15px] font-mono mt-0.5">
              {k.value}<span className="text-[10px] text-slate-500 ml-1">{k.suffix}</span>
            </p>
            <p className="text-slate-500 text-[9.5px] mt-0.5">{k.count} employé{k.count > 1 ? "s" : ""}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-10 text-center text-slate-400">Chargement…</div>
      ) : !data?.entries?.length ? (
        <div className="rounded-lg border border-slate-700 bg-slate-800/40 py-10 text-center text-slate-400 text-[13px]">
          Aucun paiement à afficher pour {MONTHS[payMonth - 1]} {payYear}.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-700 bg-slate-800/40">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-slate-900/60 text-slate-300 text-[10px] uppercase tracking-wider">
                <th className="text-left px-3 py-2 font-bold">Employé</th>
                <th className="text-left px-2 py-2 font-bold hidden sm:table-cell">Mois travaillé</th>
                <th className="text-right px-2 py-2 font-bold text-emerald-300">Net</th>
                <th className="text-left px-2 py-2 font-bold text-blue-300">
                  <CalendarDays className="w-3 h-3 inline mr-1" /> Payable le
                </th>
                <th className="text-left px-2 py-2 font-bold hidden md:table-cell">Payé le</th>
                <th className="text-center px-2 py-2 font-bold">Statut</th>
                <th className="text-right px-3 py-2 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e) => (
                <tr
                  key={e.salary_id}
                  className={`border-t border-slate-700/50 hover:bg-slate-700/20 ${e.is_paid ? "" : "bg-amber-500/[0.03]"}`}
                  data-testid={`ps-row-${e.salary_id}`}
                >
                  <td className="px-3 py-2">
                    <div className="text-white font-semibold text-[12.5px]">{e.name}</div>
                    <div className="text-slate-500 text-[10.5px]">
                      {e.role_title || "—"}
                      {e.matricule && <span className="text-slate-700"> · {e.matricule}</span>}
                    </div>
                    {e.mobile_money ? (
                      <button
                        type="button"
                        onClick={() => copyToClipboard(e.mobile_money, `${(e.name || "").split(" ")[0]} · MoMo`)}
                        className="mt-0.5 inline-flex items-center gap-1 text-[10.5px] font-mono text-amber-300 hover:text-amber-200 group"
                        title="Cliquer pour copier le numéro Mobile Money"
                        data-testid={`ps-momo-${e.salary_id}`}
                      >
                        <Smartphone className="w-3 h-3" />
                        <span className="font-bold">{e.mobile_money}</span>
                        <Copy className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ) : (
                      <p className="mt-0.5 text-[10px] text-slate-600 italic">Pas de N° Mobile Money</p>
                    )}
                  </td>
                  <td className="px-2 py-2 hidden sm:table-cell text-slate-300 text-[11px] font-semibold">
                    {workMonthLabel}
                  </td>
                  <td className="px-2 py-2 text-right font-mono font-black text-emerald-300 text-[13px]">{fmt(e.net)}</td>
                  <td className="px-2 py-2 text-blue-200 font-mono text-[11px] whitespace-nowrap">
                    {formatFrDate(e.payment_planned_date)}
                  </td>
                  <td className="px-2 py-2 hidden md:table-cell font-mono text-[11px] whitespace-nowrap">
                    {e.paid_at ? (
                      <span className="text-emerald-300">{formatFrDate(e.paid_at)}</span>
                    ) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-2 py-2 text-center">
                    {e.is_paid ? (
                      <span className="inline-flex items-center gap-1 text-emerald-300 text-[10.5px] font-bold bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/30">
                        <CheckCircle2 className="w-3 h-3" /> Payé
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-300 text-[10.5px] font-bold bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/30">
                        <MinusCircle className="w-3 h-3" /> À payer
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {e.is_paid ? (
                      <Button
                        size="sm" variant="ghost" onClick={() => unmarkPaid(e)}
                        className="h-7 px-2 text-amber-300 hover:bg-amber-500/10 text-[10.5px]"
                        data-testid={`ps-unmark-${e.salary_id}`}
                      >
                        <MinusCircle className="w-3 h-3 mr-1" /> Annuler
                      </Button>
                    ) : (
                      <Button
                        size="sm" onClick={() => setPayDialog({ entry: e })}
                        className="h-7 px-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[10.5px]"
                        data-testid={`ps-mark-paid-${e.salary_id}`}
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Marquer payé
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-600 bg-slate-900/40 font-bold">
                <td colSpan={2} className="px-3 py-2 text-slate-300 uppercase text-[10.5px] tracking-wider">
                  Total échéance {formatFrDate(data.payment_planned_date)}
                </td>
                <td className="px-2 py-2 text-right font-mono text-emerald-300 text-[13px]">{fmt(data.totals.planned)} F</td>
                <td colSpan={4} className="px-2 py-2 text-slate-500 text-[10.5px]">
                  Payé {fmt(data.totals.paid)} F · Reste{" "}
                  <span className="text-amber-300 font-mono">{fmt(data.totals.unpaid)} F</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Modale marquage payé */}
      <MarkPaidDialog
        open={!!payDialog}
        onClose={() => setPayDialog(null)}
        onConfirm={confirmMarkPaid}
        year={workYear}
        month={workMonth}
        employeeName={payDialog?.entry?.name || "—"}
        suggestedAmount={payDialog?.entry?.net || 0}
        paidBy={currentUser?.name || currentUser?.username || "Administrateur"}
      />
    </div>
  );
};

export default PaymentSchedulePanel;
